/**
 * Press-and-hold jog control (§7.2).
 *
 * Holding repeats at 8 Hz after a 350 ms delay; a single click steps once. Arrow keys on the
 * focused control do the same thing, so a gate can be positioned without a pointer.
 */

import { useCallback, useEffect, useRef } from 'react';

export function JogButtons({
  label,
  step,
  onJog,
  disabled,
  ariaLabel,
}: {
  label: string;
  step: number;
  onJog(delta: number): void;
  disabled?: boolean;
  ariaLabel: string;
}): JSX.Element {
  const timer = useRef<number | null>(null);
  const repeat = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    if (repeat.current !== null) window.clearInterval(repeat.current);
    timer.current = null;
    repeat.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const start = (delta: number): void => {
    if (disabled) return;
    onJog(delta);
    timer.current = window.setTimeout(() => {
      repeat.current = window.setInterval(() => onJog(delta), 125);
    }, 350);
  };

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }} aria-label={ariaLabel}>
      <button
        onPointerDown={() => start(-step)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            onJog(-step);
          }
        }}
        disabled={disabled}
        aria-label={`Lower ${label}`}
      >
        −
      </button>
      <button
        onPointerDown={() => start(step)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            onJog(step);
          }
        }}
        disabled={disabled}
        aria-label={`Raise ${label}`}
      >
        +
      </button>
    </div>
  );
}
